import type {
  ActivityAtSiteDto,
  CompanyCardDto,
  CompanySource,
  EconomicActivityDto,
  IdealActivityDto,
  IdealSiteDto,
  ResearchState,
  Site,
  SiteUse,
  StepGuideDto,
} from '@bk/contracts';
import { SectorReport } from './SectorReport';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { StepIntro } from './Illustration';
import { BrainNotes } from './StepReview';
import { SiteStreetView } from './Maps';
import { Needs, staffSource, useSlideIn, where } from './SitesStep';
import { DoneBand, Feeds, Glance, Notice, Pill, Row, RowGroup, useRows } from './Summary';

const FROM: Record<CompanySource, string> = {
  visura: 'dalla visura',
  persona: 'detta da te',
  documento: 'da un tuo documento',
  posta: 'dalla posta',
  sito: 'dal vostro sito',
  registro: 'da una pagina pubblica',
  online: 'da una pagina online sulla sede',
  supposto: 'supposto dal cervello',
};

/** Com'è l'attività in questa sede (ADR 0044): stato, come vi si svolge, da dove viene. */
const atSite = (a: EconomicActivityDto, site: Site): ActivityAtSiteDto | undefined =>
  a.in_sede.find((x) => x.site_id === site.id);

/** La pagina da cui viene un legame, se il riferimento è un indirizzo web. */
const pageOf = (rif: string | null) =>
  /^https?:\/\/\S+/.exec(rif ?? '')?.[0]?.replace(/[.,;]+$/, '') ?? null;

const norm = (s: string) => s.trim().toLowerCase();

const INPUT =
  'w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20';

/**
 * Una scheda del passo: l'uso di una sede (la sede legale si chiede sempre, ADR 0029) oppure le attività che vi si
 * svolgono. La sede legale anche operativa ha due schede: la domanda e poi, come una sede a sé, le sue attività.
 */
interface Slide {
  key: string;
  kind: 'uso' | 'attivita';
  site: Site;
}

const notOperational = (s: Site) => s.use === 'interna' || s.use === 'domicilio';

function slidesOf(list: Site[]): Slide[] {
  return list.flatMap((site): Slide[] => {
    const uso: Slide = { key: `uso:${site.id}`, kind: 'uso', site };
    const acts: Slide = { key: `attivita:${site.id}`, kind: 'attivita', site };
    if (site.legal) return site.use === 'operativa' ? [uso, acts] : [uso];
    return notOperational(site) ? [uso] : [acts];
  });
}

/** Come si chiama una scheda nella navigazione. */
const label = (x: Slide) =>
  x.site.legal && x.kind === 'attivita' ? `${x.site.name} · parte operativa` : x.site.name;

/**
 * Passo «Attività economiche» della configurazione guidata, giocato come «L'impresa» e «Sedi»: «cosa fate qui?». Una
 * sede alla volta, scorrendo da una all'altra. Per la sede legale prima si chiede se è anche operativa (ADR 0029): se lo
 * è segue la stessa sede con le sue attività, sennò si passa alla sede successiva. Per le altre sedi le attività che vi
 * si svolgono: un tocco per dire che sono giuste, oppure le correggi, le togli da quella sede o ne aggiungi.
 */
export function ActivitiesStep({
  next,
  onNext,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
}) {
  const { t, isManager } = useTenant();
  const qc = useQueryClient();
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const guide = useTenantQuery<StepGuideDto>(['guide', 'attivita'], '/onboarding/guide/attivita');
  const list = (sites.data?.data ?? []).filter((s) => s.status !== 'chiusa');
  const slides = slidesOf(list);
  const all = card.data?.attivita ?? [];
  const live = all.filter((a) => a.stato !== 'cessata');
  const stopped = all.filter((a) => a.stato === 'cessata');
  const here = (s: Site) => live.filter((a) => a.sedi.includes(s.id));
  const ok = (x: Slide) =>
    x.kind === 'uso'
      ? x.site.use !== null
      : here(x.site).length > 0 && here(x.site).every((a) => atSite(a, x.site)?.stato === 'attiva');
  const done = slides.filter(ok).length;
  const allDone = !!slides.length && done === slides.length;
  // Tutto a posto: il riepilogo al posto delle schede (ADR 0033). «Rivedi il passo» rimette le schede.
  const [review, setReview] = useState(false);
  const summary = allDone && !review;
  const rows = useRows();
  // Cosa è cambiato altrove dopo aver cambiato l'uso di una sede dal riepilogo.
  const [ripple, setRipple] = useState<string | null>(null);
  // Nessuna sede operativa: le attività si svolgono fuori (presso i clienti, nei cantieri, online).
  const outside = !!list.length && list.every(notOperational) && live.length > 0;
  const ideal = guide.data?.ideale;
  const advice = ideal?.impresa?.voci ?? [];

  // La scheda aperta si ricorda per chiave: le schede cambiano quando si risponde sull'uso di una sede.
  const [cur, setCur] = useState<string | null>(null);
  const [want, setWant] = useState<string | null>(null);
  const [dir, setDir] = useState(1);
  const ready = !!card.data && !!sites.data;
  // Si parte dalla prima scheda ancora da sistemare; poi decide la persona.
  useEffect(() => {
    if (cur || !ready || !slides.length) return;
    setCur((slides.find((x) => !ok(x)) ?? slides[0]!).key);
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps
  // Dopo una risposta si va alla scheda giusta appena c'è.
  useEffect(() => {
    if (want && slides.some((x) => x.key === want)) {
      setDir(1);
      setCur(want);
      setWant(null);
    }
  }, [want, slides.map((x) => x.key).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const found = slides.findIndex((x) => x.key === cur);
  const at = found >= 0 ? found : 0;
  const show = (k: number) => {
    if (k < 0 || k >= slides.length || k === at) return;
    setDir(k > at ? 1 : -1);
    setCur(slides[k]!.key);
  };
  // Frecce della tastiera per scorrere le schede, se non si sta scrivendo.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('input, textarea, select, [contenteditable]')) return;
      if (e.key === 'ArrowRight') show(at + 1);
      if (e.key === 'ArrowLeft') show(at - 1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });
  const touch = useRef<number | null>(null);
  const slide = slides[at];

  const setUse = useMutation({
    mutationFn: ({ site, use }: { site: Site; use: SiteUse }) =>
      api(t(`/sites/${site.id}`), { method: 'PATCH', body: { use } }),
    onSuccess: (_, { site, use }) => {
      void qc.invalidateQueries();
      if (summary) {
        rows.close(true);
        setRipple(
          use === 'domicilio'
            ? `${site.name} è solo un indirizzo: non compare più negli impianti, perché lì non lavorate.`
            : `${site.name}: ${USE_WORD[use]}. Ricontrollo da solo gli impianti di questa sede, li trovi nel passo «Impianti e attrezzature».`,
        );
      }
      // Operativa: la stessa sede con le sue attività. Non operativa: la sede successiva (o la sua domanda).
      if (use === 'operativa') setWant(`attivita:${site.id}`);
      else {
        const after = list[list.findIndex((s) => s.id === site.id) + 1];
        setWant(after ? slidesOf([after])[0]!.key : `uso:${site.id}`);
      }
    },
  });

  return (
    <div className="pb-8 pt-2">
      <StepIntro
        step="attivita"
        title={
          summary
            ? 'Cosa fa la tua azienda, e dove'
            : list.length > 1
              ? 'Cosa fate in ogni sede?'
              : 'Cosa fate nella tua sede?'
        }
      >
        <p className="mt-1 text-sm text-ink/60">
          {summary
            ? 'Le attività sede per sede. Per cambiarne una tocca «Modifica»: si apre solo quella.'
            : 'Una sede alla volta: dimmi se le attività sono giuste. Un tocco per ognuna, e se qualcosa non va la correggi tu.'}
        </p>
        {slides.length > 0 && !summary && (
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            aria-label={`${done} di ${slides.length} a posto`}
          >
            {slides.map((x, k) => (
              <button
                key={x.key}
                title={label(x)}
                aria-label={`${label(x)}${ok(x) ? ' (a posto)' : ''}`}
                aria-current={k === at ? 'true' : undefined}
                onClick={() => show(k)}
                className={`h-2 w-8 rounded-full transition-colors ${ok(x) ? 'bg-emerald-500' : 'bg-black/10'} ${k === at ? 'ring-2 ring-accent/50 ring-offset-1' : ''}`}
              />
            ))}
            <span className="ml-2 text-xs tabular-nums text-ink/55">
              {done} di {slides.length}
            </span>
          </div>
        )}
      </StepIntro>
      <BrainNotes step="attivita" d={ideal ?? null} what="le attività" />

      {(card.isLoading || sites.isLoading) && (
        <p className="py-6 text-center text-sm text-ink/50">
          Raccolgo le attività della tua azienda…
        </p>
      )}
      {!sites.isLoading && !list.length && (
        <p className="mt-6 rounded-2xl border-2 border-dashed border-amber-300 bg-white px-4 py-3 text-sm">
          Non ci sono ancora sedi: torna al passo «Sedi» e aggiungi dove lavora la tua azienda, poi
          qui mi dici cosa fate in ognuna.
        </p>
      )}

      {review && allDone && (
        <p className="mt-4 text-center">
          <button className="text-sm text-accent hover:underline" onClick={() => setReview(false)}>
            ← Torna al riepilogo
          </button>
        </p>
      )}

      {summary && card.data && (
        <ActivitiesSummary
          list={list}
          live={live}
          here={here}
          rows={rows}
          manager={isManager}
          saving={setUse.isPending}
          onUse={(site, use) => setUse.mutate({ site, use })}
          onReview={isManager ? () => setReview(true) : null}
        />
      )}
      {summary && ripple && <Notice>{ripple}</Notice>}

      {slide && card.data && !summary && (
        <>
          {slides.length > 1 && (
            <div className="mt-6 flex items-center gap-2">
              <button
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-lg shadow-sm ring-1 ring-black/5 transition hover:ring-accent/40 disabled:opacity-30"
                aria-label="Indietro"
                disabled={at === 0}
                onClick={() => show(at - 1)}
              >
                ‹
              </button>
              <p className="min-w-0 flex-1 text-center text-xs text-ink/55">
                <span className="font-semibold tabular-nums">{at + 1}</span> di{' '}
                <span className="tabular-nums">{slides.length}</span>
              </p>
              <button
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-lg shadow-sm ring-1 ring-black/5 transition hover:ring-accent/40 disabled:opacity-30"
                aria-label="Avanti"
                disabled={at === slides.length - 1}
                onClick={() => show(at + 1)}
              >
                ›
              </button>
            </div>
          )}
          <div
            className={slides.length > 1 ? 'mt-3' : 'mt-6'}
            onTouchStart={(e) => (touch.current = e.touches[0]?.clientX ?? null)}
            onTouchEnd={(e) => {
              const x = e.changedTouches[0]?.clientX;
              if (touch.current != null && x != null && Math.abs(x - touch.current) > 60)
                show(x < touch.current ? at + 1 : at - 1);
              touch.current = null;
            }}
          >
            {slide.kind === 'uso' ? (
              <UseSlide
                key={slide.key}
                site={slide.site}
                dir={dir}
                mine={here(slide.site)}
                manager={isManager}
                saving={setUse.isPending}
                onUse={(use) => setUse.mutate({ site: slide.site, use })}
              />
            ) : (
              <SiteSlide
                key={slide.key}
                site={slide.site}
                dir={dir}
                acts={live}
                ideal={ideal?.sedi.find((x) => x.sede_id === slide.site.id) ?? null}
                manager={isManager}
                onBack={slide.site.legal ? () => show(at - 1) : null}
                onNotOperational={
                  slide.site.legal
                    ? null
                    : () => setUse.mutate({ site: slide.site, use: 'interna' })
                }
              />
            )}
            <ErrorText>{setUse.error ? errorMessage(setUse.error) : ''}</ErrorText>
          </div>
          {at < slides.length - 1 && (
            <button
              className="mt-3 w-full rounded-2xl bg-accent/[0.06] px-4 py-3 text-sm font-medium text-accent transition hover:bg-accent/10"
              onClick={() => show(at + 1)}
            >
              Avanti: {label(slides[at + 1]!)} →
            </button>
          )}
        </>
      )}

      {outside && (
        <p className="mt-5 rounded-2xl bg-accent/[0.05] px-4 py-3 text-sm text-ink/75">
          Hai risposto che in nessuna sede lavorate per i clienti: quindi{' '}
          {live.map((a) => a.nome).join(', ')} le fate fuori, presso i clienti, nei cantieri o
          online. Se non è così, tocca «Modifica» sulla sede e scegli «Sì, qui lavoriamo per i
          clienti».
        </p>
      )}

      {stopped.length > 0 && (
        <p className="mt-4 text-xs text-ink/45">
          Non le fate più:{' '}
          {stopped.map((a, k) => (
            <span key={a.id}>
              {k > 0 && ', '}
              {a.nome} {isManager && <Restore a={a} />}
            </span>
          ))}
        </p>
      )}

      {advice.length > 0 && (
        <details className="group mt-5 text-sm">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-xs text-ink/50 [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90">▶</span>
            Come ti consiglio di organizzare le attività
            {ideal?.impresa?.generating && <span>· lo aggiorno con le tue risposte…</span>}
          </summary>
          <Needs items={advice} />
        </details>
      )}

      {summary && (
        <Feeds steps={['Impianti e attrezzature', 'Organizzazione e ruoli', 'Procedure']} />
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
      </div>
    </div>
  );
}

const USE_WORD: Record<SiteUse, string> = {
  operativa: 'si lavora per i clienti',
  interna: 'solo direzione e amministrazione',
  domicilio: 'solo un indirizzo',
};

/**
 * Il riepilogo del passo a conferme finite (ADR 0033): i numeri in breve, poi sede per sede le attività che vi si
 * svolgono, e in fondo le sedi dove non si lavora per i clienti. Ogni riga si apre da sola nella sua scheda.
 */
function ActivitiesSummary({
  list,
  live,
  here,
  rows,
  manager,
  saving,
  onUse,
  onReview,
}: {
  list: Site[];
  live: EconomicActivityDto[];
  here: (s: Site) => EconomicActivityDto[];
  rows: ReturnType<typeof useRows>;
  manager: boolean;
  saving: boolean;
  onUse: (site: Site, use: SiteUse) => void;
  onReview: (() => void) | null;
}) {
  const working = list.filter((s) => !notOperational(s));
  const idle = list.filter(notOperational);
  // Fuori sede solo ciò che non è legato a nessuna sede: un'attività che la persona ha detto «Non qui» non va altrove.
  const nowhere = live.filter((a) => !a.in_sede.some((l) => list.some((s) => s.id === l.site_id)));
  const useRow = (site: Site) => (
    <Row
      key={`uso:${site.id}`}
      icon={site.use === 'domicilio' ? '📮' : site.use === 'interna' ? '🗂️' : '🏭'}
      open={rows.open === `uso:${site.id}`}
      edited={rows.edited === `uso:${site.id}`}
      onEdit={manager ? () => rows.toggle(`uso:${site.id}`) : null}
      editor={
        <UseSlide
          site={site}
          dir={0}
          mine={here(site)}
          manager={manager}
          saving={saving}
          onUse={(use) => (use === site.use ? rows.close() : onUse(site, use))}
        />
      }
    >
      <p className="font-medium">
        {site.legal ? 'Sede legale: cosa si fa qui' : site.name}
        {site.use && (
          <Pill tone={site.use === 'operativa' ? 'green' : 'gray'}>{USE_WORD[site.use]}</Pill>
        )}
      </p>
      <p className="text-sm text-ink/60">{where(site) ?? site.name}</p>
    </Row>
  );
  const actRow = (a: EconomicActivityDto, site: Site) => {
    const key = `${site.id}:${a.id}`;
    return (
      <Row
        key={key}
        icon={a.icona ?? (a.prevalente ? '⭐' : '🏷️')}
        open={rows.open === key}
        edited={rows.edited === key}
        onEdit={manager ? () => rows.toggle(key) : null}
        editor={
          <ul>
            <ActivityRow a={a} site={site} ideal={null} updating={false} manager={manager} />
          </ul>
        }
      >
        <p className="font-medium">{a.nome}</p>
        {atSite(a, site)?.descrizione && (
          <p className="text-sm text-ink/60">{atSite(a, site)!.descrizione}</p>
        )}
        <p className="text-xs text-ink/45">
          {[
            a.prevalente ? 'attività principale' : null,
            // Il codice come dato della visura solo se la visura lo scrive per la sede (ADR 0083).
            a.ateco ? (inSiteVisura(a.ateco, site) ? `ATECO ${a.ateco} dalla visura` : `voce standard ${a.ateco}`) : null,
            FROM[atSite(a, site)?.fonte ?? a.fonte],
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </Row>
    );
  };
  const addRow = (site: Site) => {
    const key = `nuova:${site.id}`;
    return (
      <Row
        key={key}
        icon="＋"
        open={rows.open === key}
        onEdit={() => rows.toggle(key)}
        editLabel="Aggiungi"
        editor={
          <>
            <p className="mb-2 text-sm font-medium">Un’attività in più per {site.name}</p>
            <ActivityEdit site={site} onDone={() => rows.close()} />
          </>
        }
      >
        <p className="text-sm text-ink/55">Manca un’attività in questa sede?</p>
      </Row>
    );
  };
  return (
    <div className="mt-6">
      <DoneBand title="Le attività sono a posto" onReview={onReview} />
      <Glance
        tiles={[
          [live.length, live.length === 1 ? 'attività' : 'attività in tutto'],
          [working.length, working.length === 1 ? 'sede dove lavorate per i clienti' : 'sedi dove lavorate per i clienti'],
          idle.length > 0 && [
            idle.length,
            idle.length === 1 ? 'sede solo per direzione o indirizzo' : 'sedi solo per direzione o indirizzo',
          ],
        ]}
      />
      {working.map((site) => (
        <RowGroup
          key={site.id}
          title={`📍 ${site.name}`}
          sub={[site.legal ? 'sede legale' : null, where(site)].filter(Boolean).join(' · ')}
        >
          {site.legal && useRow(site)}
          {here(site).map((a) => actRow(a, site))}
          {manager && addRow(site)}
        </RowGroup>
      ))}
      {nowhere.length > 0 && (
        <RowGroup title="Fuori sede" sub="presso i clienti, nei cantieri o online">
          {nowhere.map((a) => (
            <Row key={a.id} icon="🧭">
              <p className="font-medium">{a.nome}</p>
            </Row>
          ))}
        </RowGroup>
      )}
      {idle.length > 0 && (
        <RowGroup title="Dove non lavorate per i clienti" sub="qui solo direzione, amministrazione o un indirizzo">
          {idle.map(useRow)}
        </RowGroup>
      )}
    </div>
  );
}

/** Chi è la sede: nome, indirizzo, addetti, con la vista dalla strada. */
function SiteHeader({ site, eyebrow }: { site: Site; eyebrow: string }) {
  const address = where(site);
  return (
    <div className="flex flex-col-reverse border-b border-black/5 sm:grid sm:grid-cols-[minmax(0,1fr)_13rem]">
      <div className="min-w-0 px-4 py-3">
        <p className="text-xs text-ink/50">{eyebrow}</p>
        <p className="text-base font-medium">📍 {site.name}</p>
        {address && <p className="text-sm text-ink/70">{address}</p>}
        {site.employees != null && (
          <p className="mt-1 text-xs text-ink/50">
            Ci lavorano {site.employees} {site.employees === 1 ? 'persona' : 'persone'}
            {staffSource(site) && ` · ${staffSource(site)}`}
          </p>
        )}
      </div>
      <SiteStreetView site={site} className="h-32 w-full sm:h-full sm:min-h-[8rem]" />
    </div>
  );
}

const USES: Array<{ use: SiteUse; icon: string; title: string; text: string }> = [
  {
    use: 'operativa',
    icon: '🏭',
    title: 'Sì, qui lavoriamo per i clienti',
    text: 'Qui facciamo il lavoro che vendiamo: produzione, vendita, servizi, anche in ufficio (software, progetti, pratiche).',
  },
  {
    use: 'interna',
    icon: '🗂️',
    title: 'No, qui solo direzione e amministrazione',
    text: 'Il lavoro per i clienti lo facciamo altrove: presso i clienti, nei cantieri o in un’altra sede.',
  },
  {
    use: 'domicilio',
    icon: '📮',
    title: 'No, è solo un indirizzo',
    text: 'Domiciliazione presso il commercialista o un centro servizi: qui non lavoriamo.',
  },
];

/** La domanda sull'uso della sede (ADR 0029): per la sede legale sempre, per le altre se segnate non operative. */
function UseSlide({
  site,
  dir,
  mine,
  manager,
  saving,
  onUse,
}: {
  site: Site;
  dir: number;
  mine: EconomicActivityDto[];
  manager: boolean;
  saving: boolean;
  onUse: (use: SiteUse) => void;
}) {
  const box = useSlideIn(dir);
  return (
    <div
      ref={box}
      className={`overflow-hidden rounded-2xl border-2 bg-white transition-colors ${site.use ? 'border-emerald-400/60' : 'border-black/5'}`}
    >
      <SiteHeader site={site} eyebrow={site.legal ? 'Sede legale' : 'Sede'} />
      <div className="px-4 py-3">
        <p className="text-base font-medium">
          {site.legal ? 'Nella sede legale lavorate anche per i clienti?' : 'Qui non lavorate per i clienti: è giusto?'}
        </p>
        <p className="mt-1 text-sm text-ink/60">
          {site.legal
            ? 'La sede legale è l’indirizzo dell’impresa scritto nel registro. A volte lì si fanno solo direzione e amministrazione, a volte anche il lavoro che vendete.'
            : 'Ho capito che qui fate solo direzione e amministrazione, o che è solo un indirizzo.'}
        </p>
        {site.activity && (
          <p className="mt-2 text-xs text-ink/50">
            Cosa fate qui ({FROM[site.source]}): {site.activity}
          </p>
        )}
        <div className="mt-3 grid gap-2">
          {USES.map((u) => {
            const picked = site.use === u.use;
            return (
              <button
                key={u.use}
                disabled={!manager || saving}
                aria-pressed={picked}
                onClick={() => onUse(u.use)}
                className={`flex items-start gap-3 rounded-xl border-2 px-3 py-2.5 text-left transition disabled:cursor-default ${picked ? 'border-emerald-400/70 bg-emerald-50/60' : 'border-black/5 enabled:hover:border-accent/40'}`}
              >
                <span className="text-xl leading-none">{u.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">
                    {u.title}
                    {picked && <span className="ml-2 text-emerald-700">✓</span>}
                  </span>
                  <span className="block text-xs text-ink/55">{u.text}</span>
                  {u.use !== 'operativa' && !picked && mine.length > 0 && (
                    <span className="mt-0.5 block text-xs text-amber-700">
                      Con questa risposta togli da questa sede: {mine.map((a) => a.nome).join(', ')}.
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Una sede: chi è (con la vista dalla strada), cosa vi si fa e cosa si può aggiungere. */
export function SiteSlide({
  site,
  dir,
  acts,
  ideal,
  manager,
  onBack,
  onNotOperational,
}: {
  site: Site;
  dir: number;
  acts: EconomicActivityDto[];
  ideal: IdealSiteDto | null;
  manager: boolean;
  /** Sede legale: torna alla domanda sull'uso. */
  onBack: (() => void) | null;
  /** Altre sedi: segnarla non operativa. */
  onNotOperational: (() => void) | null;
}) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const box = useSlideIn(dir);
  const [adding, setAdding] = useState(false);
  const mine = acts.filter((a) => a.sedi.includes(site.id));
  const others = acts.filter((a) => !a.sedi.includes(site.id));
  const confirmed = mine.length > 0 && mine.every((a) => atSite(a, site)?.stato === 'attiva');
  // Si fa anche qui: confermata in questa sede, le altre sedi restano com'erano (ADR 0044).
  const join = useMutation({
    mutationFn: (a: EconomicActivityDto) =>
      api(t(`/company/activities/${a.id}/sites/${site.id}`), {
        method: 'PATCH',
        body: { stato: 'attiva' },
      }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  return (
    <div
      ref={box}
      className={`overflow-hidden rounded-2xl border-2 bg-white transition-colors ${confirmed ? 'border-emerald-400/60' : 'border-black/5'}`}
    >
      <SiteHeader site={site} eyebrow={site.legal ? 'Sede legale · parte operativa' : 'Sede'} />

      <div className="px-4 py-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent/80">
          Cosa fate qui
        </p>
        {!mine.length && (
          <p className="mb-2 rounded-xl border-2 border-dashed border-amber-300 px-3 py-2.5 text-sm">
            Non so ancora cosa fate in questa sede:{' '}
            {others.length
              ? 'scegli tra le attività qui sotto o aggiungine una.'
              : 'aggiungila qui sotto.'}
          </p>
        )}
        {GROUPS.map((g) => {
          const items = mine.filter((a) => g.has(atSite(a, site)?.prova ?? null));
          if (!items.length) return null;
          return (
            <section key={g.key} className="mt-4 first:mt-0" aria-label={g.title}>
              <p className="mb-2 flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-semibold">{g.title}</span>
                <span className="text-xs text-ink/50">{g.sub}</span>
              </p>
              {g.note && (
                <p className="mb-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  {g.note}
                </p>
              )}
              <ul className="grid gap-2.5 sm:grid-cols-2">
                {items.map((a) => (
                  <ActivityRow
                    key={a.id}
                    a={a}
                    site={site}
                    ideal={ideal?.attivita.find((x) => norm(x.attivita) === norm(a.nome)) ?? null}
                    updating={!!ideal?.generating}
                    manager={manager}
                  />
                ))}
              </ul>
            </section>
          );
        })}

        {manager && others.length > 0 && (
          <div className="mt-4">
            <p className="mb-1.5 text-xs text-ink/50">
              {mine.length
                ? 'Si fa qui anche…'
                : 'Attività dell’impresa: tocca quelle che fate qui'}
            </p>
            <div className="flex flex-wrap gap-2">
              {others.map((a) => (
                <button
                  key={a.id}
                  className="rounded-full border border-black/10 px-3 py-1 text-sm text-ink/70 transition hover:border-accent hover:text-accent disabled:opacity-50"
                  disabled={join.isPending}
                  onClick={() => join.mutate(a)}
                >
                  ＋ {a.nome}
                </button>
              ))}
            </div>
          </div>
        )}

        {manager &&
          (adding ? (
            <div className="mt-3 rounded-xl border-2 border-accent/30 px-3 py-3">
              <p className="mb-2 text-sm font-medium">Un’attività in più per {site.name}</p>
              <ActivityEdit site={site} onDone={() => setAdding(false)} />
            </div>
          ) : (
            <button
              className="mt-3 w-full rounded-xl border-2 border-dashed border-black/10 px-3 py-2.5 text-sm text-ink/60 transition hover:border-accent hover:text-accent"
              onClick={() => setAdding(true)}
            >
              ＋ {mine.length ? 'Manca un’attività? Aggiungila' : 'Aggiungi l’attività'}
            </button>
          ))}
        {manager && (onBack || onNotOperational) && (
          <p className="mt-3 text-xs">
            {onBack && (
              <button className="text-ink/45 hover:text-accent hover:underline" onClick={onBack}>
                ← Qui non lavorate per i clienti? Cambia la risposta
              </button>
            )}
            {onNotOperational && (
              <button
                className="text-ink/45 hover:text-accent hover:underline"
                onClick={onNotOperational}
              >
                Qui non lavorate per i clienti? Solo direzione e amministrazione, o solo un indirizzo
              </button>
            )}
          </p>
        )}
        <Sources site={site} manager={manager} />
        <ErrorText>{join.error ? errorMessage(join.error) : ''}</ErrorText>
      </div>
    </div>
  );
}

/** Le cifre di un codice ATECO, per confrontarlo: «56.30.00 – Bar» → «563000». */
const digits = (v: string | null | undefined) => /^\d{2}(?:\.\d{1,2}){0,2}/.exec(v?.trim() ?? '')?.[0]?.replace(/\D/g, '') ?? '';

/**
 * Il codice è tra quelli che la visura scrive per la sede (ADR 0083): stesso ramo, cioè uno inizia con l'altro, con
 * almeno tre cifre. Solo allora si mostra come dato della visura.
 */
export function inSiteVisura(code: string | null | undefined, site: Pick<Site, 'ateco_primary' | 'ateco_secondary'>): boolean {
  const x = digits(code);
  if (x.length < 3) return false;
  return [site.ateco_primary, ...site.ateco_secondary].some((c) => {
    const y = digits(c);
    const [short, long] = x.length <= y.length ? [x, y] : [y, x];
    return short.length >= 3 && long.startsWith(short);
  });
}

/** I segni delle prove di un'attività in una sede (ADR 0045): codice della visura, pagina online, scheda sulle mappe. */
function proofsOf(a: EconomicActivityDto, link: ActivityAtSiteDto | undefined, site: Site) {
  const page = pageOf(link?.rif ?? null);
  const host = page ? page.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]! : null;
  const maps = !!page && /google\.[a-z.]+\/maps|maps\.google|goo\.gl\/maps/i.test(page);
  const code = /^\d{2}(?:\.\d{1,2}){0,2}/.exec(a.ateco ?? '')?.[0] ?? null;
  // Un codice che la visura della sede non scrive è la voce standard dell'attività (ADR 0083): mai con il segno della visura.
  const certain = !!code && link?.prova !== 'solo_online' && inSiteVisura(code, site);
  return {
    code: certain ? code : null,
    proposed: !certain ? code : null,
    page,
    label: maps ? 'Mappe' : host,
    maps,
    quote: link?.rif?.split(' — ').slice(1).join(' — ').trim() || null,
  };
}

const CHIP = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold';

/**
 * Un'attività in una sede, come tessera (ADR 0045): icona, nome, come si svolge lì, le prove con il link alla fonte;
 * «Giusta» e «Non qui» valgono solo per questa sede.
 */
function ActivityRow({
  a,
  site,
  ideal,
  updating,
  manager,
}: {
  a: EconomicActivityDto;
  site: Site;
  ideal: IdealActivityDto | null;
  /** Il progetto della sede si sta riscrivendo con le risposte date: intanto resta quello di prima. */
  updating: boolean;
  manager: boolean;
}) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(t(`/company/activities/${a.id}`), { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  // Conferma ed esclusione valgono solo per questa sede (ADR 0044).
  const here = useMutation({
    mutationFn: (stato: ActivityAtSiteDto['stato']) =>
      api(t(`/company/activities/${a.id}/sites/${site.id}`), { method: 'PATCH', body: { stato } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  const link = atSite(a, site);
  const proof = proofsOf(a, link, site);
  const busy = update.isPending || here.isPending;
  const confirmed = link?.stato === 'attiva';
  const onlyVisura = link?.prova === 'solo_visura';
  const text = link?.descrizione ?? a.descrizione;
  const needs = ideal?.voci ?? [];
  // Le fasi in ordine, poi ciò che l'attività chiede in questo luogo verso qualsiasi ente (ADR 0133).
  const phases = needs.filter((v) => v.gruppo?.toLowerCase() === 'fasi');
  const duties = needs.filter((v) => v.gruppo?.toLowerCase() !== 'fasi');
  return (
    <li
      className={`flex flex-col gap-2.5 rounded-2xl bg-white p-3.5 transition ${confirmed ? 'ring-2 ring-emerald-500/70' : 'ring-1 ring-black/10'}`}
    >
      {editing ? (
        <ActivityEdit site={site} a={a} onDone={() => setEditing(false)} />
      ) : (
        <>
          <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-3">
            <span
              className="grid h-11 w-11 place-items-center rounded-xl bg-black/[.04] text-2xl"
              aria-hidden
            >
              {a.icona ?? '🏷️'}
            </span>
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-x-2 text-[15px] font-semibold leading-snug">
                {a.nome}
                {confirmed && (
                  <span className="text-xs font-medium text-emerald-700">✓ Giusta</span>
                )}
                {a.prevalente && (
                  <span className="text-xs font-medium text-ink/45">principale</span>
                )}
              </p>
              {onlyVisura ? (
                <p className="mt-0.5 text-sm text-ink/60">
                  Registrata per questa sede, ma nessuna pagina ne parla. Si fa ancora?
                </p>
              ) : (
                text && <p className="mt-0.5 text-sm text-ink/60">{text}</p>
              )}
            </div>
          </div>
          {/* Quanto pesa nel lavoro della sede (ADR 0089): l'ipotesi del consulente, che si corregge in «Parliamone». */}
          {link?.peso != null && (
            <div className="flex items-center gap-2" title="Quanto pesa nel lavoro della sede: ipotesi del consulente">
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                <span className="block h-full rounded-full bg-accent/70" style={{ width: `${link.peso}%` }} />
              </span>
              <span className="w-24 text-right text-xs tabular-nums text-ink/55">{link.peso}% del lavoro</span>
            </div>
          )}
          {(proof.code || proof.proposed || proof.page) && (
            <p className="flex flex-wrap gap-1.5">
              {proof.proposed && (
                <span
                  className={`${CHIP} bg-black/[0.04] text-ink/55`}
                  title="Il codice ATECO che corrisponde a questa attività: la visura non lo scrive per la sede, e non serve se è un servizio di un’attività già registrata"
                >
                  voce standard {proof.proposed}
                </span>
              )}
              {proof.code && (
                <span
                  className={`${CHIP} bg-violet-50 text-violet-700`}
                  title="Codice ATECO della sede nella visura"
                >
                  📄 {proof.code}
                </span>
              )}
              {proof.page && (
                <a
                  className={`${CHIP} ${proof.maps ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'} hover:underline`}
                  href={proof.page}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={FROM[link?.fonte ?? a.fonte]}
                >
                  {proof.maps ? '📍' : '🌐'} {proof.label}
                </a>
              )}
            </p>
          )}
          {proof.quote && (
            <details className="border-t border-black/5 pt-2 text-xs">
              <summary className="cursor-pointer text-ink/50">La prova</summary>
              <blockquote className="mt-1.5 rounded-r-lg border-l-2 border-black/10 bg-black/[.03] px-2.5 py-1.5 text-ink/70">
                «{proof.quote}»
              </blockquote>
            </details>
          )}
          {manager && (
            <div className="mt-auto flex flex-wrap items-center gap-2">
              {confirmed ? (
                <>
                  <button
                    className="text-xs text-accent hover:underline"
                    onClick={() => setEditing(true)}
                  >
                    cambia
                  </button>
                  <button
                    className="text-xs text-ink/45 hover:text-ink/70 hover:underline disabled:opacity-50"
                    disabled={busy}
                    onClick={() => here.mutate('da_confermare')}
                  >
                    annulla conferma
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="rounded-full bg-emerald-50 px-3.5 py-1.5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                    disabled={busy}
                    onClick={() => here.mutate('attiva')}
                  >
                    {onlyVisura ? 'Sì, si fa' : '👍 Giusta'}
                  </button>
                  {!onlyVisura && (
                    <button
                      className="rounded-full bg-black/5 px-3.5 py-1.5 text-sm font-semibold text-ink/70 transition hover:bg-black/10"
                      onClick={() => setEditing(true)}
                    >
                      ✏️ Correggi
                    </button>
                  )}
                </>
              )}
              <button
                className="px-1 py-1.5 text-xs text-ink/45 hover:text-red-700 hover:underline disabled:opacity-50"
                disabled={busy}
                onClick={() => here.mutate('esclusa')}
              >
                {onlyVisura ? 'Non più' : 'Non qui'}
              </button>
              <button
                className="px-1 py-1.5 text-xs text-ink/35 hover:text-red-700 hover:underline disabled:opacity-50"
                disabled={busy}
                onClick={() => update.mutate({ stato: 'cessata' })}
                title="Non la fate più in nessuna sede"
              >
                Non la fate più
              </button>
            </div>
          )}
        </>
      )}
      {!editing && (ideal?.come_si_svolge || needs.length > 0) && (
        <details className="group border-t border-black/5 pt-2">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm [&::-webkit-details-marker]:hidden">
            <span className="text-xs text-ink/40 transition-transform group-open:rotate-90">▶</span>
            <span className="font-medium text-ink/75">Come dovrebbe svolgersi qui</span>
            {updating ? (
              <span className="text-xs text-ink/45">lo aggiorno con le tue risposte…</span>
            ) : (
              needs.length > 0 && <span className="text-xs text-ink/45">{needs.length}</span>
            )}
          </summary>
          {ideal?.come_si_svolge && (
            <p className="mt-2 pl-5 text-sm text-ink/70">{ideal.come_si_svolge}</p>
          )}
          {phases.length > 0 && (
            <ol className="mt-2 space-y-1.5 pl-5 text-sm">
              {phases.map((v, i) => (
                <li key={v.nome} className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-2">
                  <span className="text-xs tabular-nums text-ink/40">{i + 1}.</span>
                  <span>
                    <span className="text-ink/80">{v.nome}</span>
                    {v.descrizione && <span className="block text-xs text-ink/50">{v.descrizione}</span>}
                    {v.area_atlante && (
                      <span className="block text-[11px] text-ink/40">
                        Atlante del Lavoro {v.area_atlante.codice}: {v.area_atlante.nome}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {duties.length > 0 && <Needs items={duties} />}
        </details>
      )}
      {!editing && <SectorReport ateco={a.ateco} />}
      <ErrorText>
        {update.error ? errorMessage(update.error) : here.error ? errorMessage(here.error) : ''}
      </ErrorText>
    </li>
  );
}

/** I gruppi delle tessere di una sede, nell'ordine delle prove (ADR 0045). */
const GROUPS: Array<{
  key: string;
  title: string;
  sub: string;
  note?: string;
  has: (p: ActivityAtSiteDto['prova']) => boolean;
}> = [
  {
    key: 'dati',
    title: 'Confermate dai dati',
    sub: 'nella visura e trovate online, o dette da te',
    has: (p) => p === 'visura_e_online' || p === null,
  },
  {
    key: 'online',
    title: 'Trovate online, non nella visura',
    sub: 'il registro non le dice',
    note: 'Per queste attività la visura non ha un codice ATECO: se le confermi, conviene aggiungerlo con il commercialista.',
    has: (p) => p === 'solo_online',
  },
  {
    key: 'visura',
    title: 'Nella visura, senza tracce online',
    sub: 'nessuna pagina ne parla',
    has: (p) => p === 'solo_visura',
  },
];

/** «Dove ho cercato» (ADR 0045): i siti usati per questa sede, perché valgono, e «non è nostro». */
function Sources({ site, manager }: { site: Site; manager: boolean }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const research = useTenantQuery<ResearchState>(['research'], '/onboarding/research');
  const act = useMutation({
    mutationFn: ({ dominio, rimetti }: { dominio: string; rimetti: boolean }) =>
      api<ResearchState>(t(`/onboarding/research/domains/${rimetti ? 'include' : 'exclude'}`), {
        body: { dominio },
      }),
    onSuccess: (r) => {
      qc.setQueryData(['tenant', tenant.id, 'research'], r);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'company'] });
    },
  });
  const list = (research.data?.fonti ?? []).filter(
    (f) => !f.sedi.length || f.sedi.includes(site.id),
  );
  const out = research.data?.domini_esclusi ?? [];
  if (!list.length && !out.length) return null;
  return (
    <details className="mt-4 rounded-xl bg-black/[.03] px-3 py-2.5 text-sm">
      <summary className="cursor-pointer font-medium text-ink/70">
        Dove ho cercato <span className="text-xs font-normal text-ink/45">{list.length} siti</span>
      </summary>
      <ul className="mt-2 space-y-1.5">
        {list.map((f) => (
          <li key={f.dominio} className="flex flex-wrap items-baseline gap-x-2">
            <a
              className="font-medium hover:text-accent hover:underline"
              href={f.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {f.dominio}
            </a>
            <span className="text-xs text-ink/50">{f.perche}</span>
            {manager && (
              <button
                className="ml-auto text-xs text-ink/45 hover:text-red-700 hover:underline disabled:opacity-50"
                disabled={act.isPending}
                onClick={() => act.mutate({ dominio: f.dominio, rimetti: false })}
              >
                Non è nostro
              </button>
            )}
          </li>
        ))}
        {out.map((d) => (
          <li key={d} className="flex flex-wrap items-baseline gap-x-2 text-ink/45">
            <span className="line-through">{d}</span>
            <span className="text-xs">detto da te: non è vostro</span>
            {manager && (
              <button
                className="ml-auto text-xs text-accent hover:underline disabled:opacity-50"
                disabled={act.isPending}
                onClick={() => act.mutate({ dominio: d, rimetti: true })}
              >
                Rimetti
              </button>
            )}
          </li>
        ))}
      </ul>
      <ErrorText>{act.error ? errorMessage(act.error) : ''}</ErrorText>
    </details>
  );
}

/** Correggere o aggiungere un'attività con le sole cose che chiunque sa dire. Correggere vale come conferma. */
function ActivityEdit({
  site,
  a,
  onDone,
}: {
  site: Site;
  a?: EconomicActivityDto;
  onDone: () => void;
}) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState({ nome: a?.nome ?? '', ateco: a?.ateco ?? '' });
  const save = useMutation({
    mutationFn: () => {
      const ateco = v.ateco.trim();
      return a
        ? api(t(`/company/activities/${a.id}`), {
            method: 'PATCH',
            body: { nome: v.nome.trim(), ateco: ateco || null, stato: 'attiva' },
          }).then(() =>
            api(t(`/company/activities/${a.id}/sites/${site.id}`), {
              method: 'PATCH',
              body: { stato: 'attiva' },
            }),
          )
        : api(t('/company/activities'), {
            body: { nome: v.nome.trim(), ...(ateco ? { ateco } : {}), sedi: [site.id] },
          });
    },
    onSuccess: () => {
      void qc.invalidateQueries();
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (v.nome.trim()) save.mutate();
  };
  return (
    <form className="space-y-3" onSubmit={submit}>
      <label className="block text-sm">
        <span className="mb-1 block text-xs text-ink/50">Cosa fate, in parole semplici</span>
        <input
          autoFocus
          className={INPUT}
          placeholder="Es. Vendita al banco, Riparazioni, Consegne a domicilio"
          value={v.nome}
          onChange={(e) => setV({ ...v, nome: e.target.value })}
          required
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-xs text-ink/50">Codice ATECO, se lo sai</span>
        <input
          className={`${INPUT} sm:w-48`}
          value={v.ateco}
          onChange={(e) => setV({ ...v, ateco: e.target.value })}
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !v.nome.trim()}>
          Salva
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </form>
  );
}

function Restore({ a }: { a: EconomicActivityDto }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const back = useMutation({
    mutationFn: () =>
      api(t(`/company/activities/${a.id}`), { method: 'PATCH', body: { stato: 'attiva' } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  return (
    <button
      className="text-accent hover:underline disabled:opacity-50"
      disabled={back.isPending}
      onClick={() => back.mutate()}
    >
      (rimetti)
    </button>
  );
}
